import { ref } from 'vue';
import { useToast } from '@/composables/common/useToast';
import store from '@/store';
import { config } from '@/config.js';

/**
 * useCodigosInvitacion — Lógica CRUD de los códigos de invitación de una categoría.
 *
 * Sigue el patrón de useDeportistaLesiones y otros composables del módulo:
 *  - State como refs (codigos, meta, loading).
 *  - Llamadas al store con config.getVuexAlias().
 *  - Toast feedback en éxito/error.
 *
 * Endpoints usados (Vuex aliases):
 *  - back.bases.categorias.codigos_invitacion.index   → GET listado
 *  - back.bases.categorias.codigos_invitacion.crear    → POST crear lote
 *  - back.bases.categorias.codigos_invitacion.borrar   → DELETE por id
 */
export function useCodigosInvitacion() {
    const toast = useToast();

    // ==================== STATE ====================
    const codigos = ref([]);
    const meta = ref({});
    const loading = ref(false);

    /**
     * Carga el listado de códigos de invitación de una categoría.
     * @param {Object} filters
     * @param {number|string} filters.listaId
     * @param {number|string} filters.categoriaId
     * @param {number} [filters.page=1]
     * @param {number} [filters.per_page=10]
     */
    async function fetch(filters = {}) {
        loading.value = true;
        try {
            const params = {
                lista_id: filters.listaId,
                categoria_id: filters.categoriaId,
                filters: {
                    page: filters.page || 1,
                    per_page: filters.per_page || 10,
                    search: filters.search || ''
                }
            };
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.codigos_invitacion.index'),
                params
            );
            codigos.value = res.data?.data || res.data || [];
            meta.value = res.data?.meta || {};
        } catch (error) {
            console.error('Error fetching codigos invitacion:', error);
            toast.error('Error al cargar los códigos de invitación');
        } finally {
            loading.value = false;
        }
    }

    /**
     * Crea un lote de códigos de invitación.
     * @param {Object} params
     * @param {number|string} params.listaId
     * @param {number|string} params.categoriaId
     * @param {number} params.cantidad
     * @param {number} params.usos
     * @returns {Promise<boolean>} true si OK, false si error
     */
    async function crear(params) {
        loading.value = true;
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.categorias.codigos_invitacion.crear'),
                {
                    lista_id: params.listaId,
                    teatro_id: params.teatroId,
                    categoria_id: params.categoriaId,
                    cantidad_codigos: params.cantidad,
                    usos: params.usos,
                    filters: {}
                }
            );
            toast.success('Códigos creados correctamente.');
            return true;
        } catch (error) {
            console.error('Error creating codigos invitacion:', error);
            toast.error(error.response?.data?.message || 'Error al crear los códigos');
            return false;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Borra un código de invitación por id.
     * @param {Object} params
     * @param {number|string} params.listaId
     * @param {number|string} params.categoriaId
     * @param {number|string} params.codigoId
     */
    async function borrar(params) {
        loading.value = true;
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.categorias.codigos_invitacion.borrar'),
                {
                    lista_id: params.listaId,
                    categoria_id: params.categoriaId,
                    codigo_id: params.codigoId
                }
            );
            toast.success('Código eliminado correctamente.');
            return true;
        } catch (error) {
            console.error('Error deleting codigo invitacion:', error);
            toast.error(error.response?.data?.message || 'Error al eliminar el código');
            return false;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Edita un código de invitación — actualmente solo permite cambiar `usos`.
     * @param {Object} params
     * @param {number|string} params.listaId
     * @param {number|string} params.categoriaId
     * @param {number|string} params.codigoId
     * @param {number} params.usos Nuevo número de usos del código
     * @returns {Promise<boolean>}
     */
    async function editar(params) {
        loading.value = true;
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.categorias.codigos_invitacion.editar'),
                {
                    lista_id: params.listaId,
                    categoria_id: params.categoriaId,
                    codigo_id: params.codigoId,
                    usos: params.usos
                }
            );
            toast.success('Usos editados correctamente.');
            return true;
        } catch (error) {
            console.error('Error editing codigo invitacion:', error);
            toast.error(error.response?.data?.message || 'Error al editar el código');
            return false;
        } finally {
            loading.value = false;
        }
    }

    return { codigos, meta, loading, fetch, crear, borrar, editar };
}
